/* eslint-disable react/display-name */
/* eslint-disable no-unused-vars */
import { useState, useCallback, useMemo } from 'react';
import { Button, Modal, Input, Tag, Avatar, Tooltip, Radio, Space, message } from 'antd';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  PhoneOutlined, InfoCircleOutlined, WalletOutlined,
  CalendarOutlined
} from '@ant-design/icons';
import { ActionCreateByUrl } from '../../store/Actions/CommonAction';
import { ActionCreatePaymentLink } from '../../store/Actions/OrderHistoryAction';
import AdvancedTable from '../../components/AdvancedTable';
import { GetAllOrderApi } from '../../utils/api';
import moment from 'moment';
import { calculatePercentage, getStatus } from '../../utils/functions';
import { PrakurtiCirculerLogo } from '../../components/imagepath';
import Header from '../../components/Header';
import Sidebar from '../../components/Sidebar';
import { useFormik } from 'formik';
import { collectCashSchema } from '../../utils/FormValidation';
import { InputFromUser } from '../../components/Input/input';
import { calculateBilling, formatCurrency, rowPaidAmount } from '../../utils/billingUtils';

const { TextArea } = Input;

const PendingHistory = () => {
  const dispatch = useDispatch();
  const { userDetails } = useSelector(state => state?.auth);
  const [refreshTrigger, setRefreshTrigger] = useState(0); // To trigger table refresh

  // Modal State
  const [IsCollectModal, setCollectModal] = useState(null);
  const [paymentLinkType, setPaymentLinkType] = useState('Cash'); // 'Cash', 'email', 'WhatsApp Link'
  const [paymentLinkAmount, setPaymentLinkAmount] = useState('');
  const [Isloading, setIsloading] = useState(false);
  const [remark, setRemark] = useState('');

  // Derived values for Modal using centralized billing logic
  const billingInfo = calculateBilling(IsCollectModal);
  const discount = billingInfo?.discountValue || 0;
  const payableAmount = billingInfo?.netAmount || 0;
  const PendingAmt = billingInfo?.pending || 0;

  // Formik for Cash Collection
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      updatedPaidAmount: PendingAmt || '',
    },
    validationSchema: collectCashSchema,
    onSubmit: (values, { setFieldError, setErrors }) => {
      const currentPaidTotal = rowPaidAmount(IsCollectModal);
      const collectionAmount = parseFloat(values.updatedPaidAmount || 0);
      const newTotalPaid = currentPaidTotal + collectionAmount;

      const billing = calculateBilling(IsCollectModal);
      const netPayable = billing?.netAmount || 0;

      // Use a small tolerance for floating point comparisons
      const updatedStatus = newTotalPaid >= (netPayable - 1) ? 'Completed' : 'Pending';
      const Percentage = calculatePercentage(netPayable, newTotalPaid);

      const payload = {
        updatedBy: IsCollectModal?.updatedBy,
        updateByName: IsCollectModal?.updateByName,
        paymentMode: 'Cash',
        updatedPaidAmount: newTotalPaid,
        paidAmount: collectionAmount,
        completedPayment: `${Percentage}%`,
        paymentStatus: updatedStatus,
        remark: remark,
      };

      setIsloading(true);
      dispatch(
        ActionCreateByUrl(
          { Query: `admin/order/order_update/${IsCollectModal?.orderId || IsCollectModal?.id}`, obj: payload },
          Res => {
            setIsloading(false);
            if (Res?.statusCode === 200) {
              message.success('Payment collected successfully');
              setCollectModal(null);
              setRemark('');
              setRefreshTrigger(prev => prev + 1);
            } else if (Res?.statusCode === 400) {
              setErrors(Res?.data?.errors);
              message.error(Res?.message || 'Failed to update payment');
            }
          }
        )
      );
    },
  });

  const handlePaymentLinkSubmit = () => {
    if (paymentLinkType === 'Email Link' || paymentLinkType === 'WhatsApp Link') {
      if (!paymentLinkAmount || paymentLinkAmount <= 0) {
        message.error('Please enter a valid amount');
        return;
      }
      if (paymentLinkAmount > PendingAmt) {
        message.error(`Amount cannot exceed pending amount of ₹${PendingAmt}`);
        return;
      }
      setIsloading(true);
      const payload = {
        amount: paymentLinkAmount,
        orderId: IsCollectModal?.id || IsCollectModal?._id,
        bookingFor: IsCollectModal?.bookingFor,
        totalAmount: IsCollectModal?.amount,
        treatment: IsCollectModal?.additionalInfo?.treatment,
        isEmail: paymentLinkType === 'Email Link',
        user: {
          name: IsCollectModal?.additionalInfo?.customerName || IsCollectModal?.additionalInfo?.name,
          email: IsCollectModal?.additionalInfo?.email || '',
          phoneNo: IsCollectModal?.additionalInfo?.phoneNo || '',
        },
        remark: remark,
      };

      dispatch(
        ActionCreatePaymentLink(payload, res => {
          setIsloading(false);
          if (res?.success) {
            message.success(`Payment link sent successfully via ${paymentLinkType === 'Email Link' ? 'Email' : 'WhatsApp'}`);
            setCollectModal(null);
            setPaymentLinkType('Cash');
            setRemark('');
            setRefreshTrigger(prev => prev + 1);
          } else {
            message.error(res?.message || 'Failed to create payment link');
          }
        })
      );
    } else {
      formik.handleSubmit();
    }
  };

  // 1. Column Definitions
  const columns = useMemo(() => [
    {
      id: 'sr',
      name: '#',
      width: 50,
      cell: (row, index) => (
        <span style={{ color: '#888' }}>{index + 1}</span>
      ),
    },
    {
      id: 'customer',
      name: 'Customer',
      selector: row => row?.additionalInfo?.name || row?.additionalInfo?.customerName,
      minWidth: 200,
      cell: row => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <Link
            to={`/order-history-overview/${row?.id || row?._id}`}
            style={{ fontWeight: 600, color: '#1890ff', fontSize: '14px' }}
          >
            {row?.additionalInfo?.name || row?.additionalInfo?.customerName || '--'}
          </Link>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#666' }}>
            <PhoneOutlined style={{ fontSize: '11px' }} />
            {row?.additionalInfo?.phoneNo || '--'}
          </div>
          {row?.additionalInfo?.email && (
            <div style={{ fontSize: '11px', color: '#999', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '180px' }}>
              {row?.additionalInfo?.email}
            </div>
          )}
        </div>
      ),
      format: (value, row) => {
        const name = row?.additionalInfo?.name || row?.additionalInfo?.customerName || '--';
        const phone = row?.additionalInfo?.phoneNo || '--';
        const email = row?.additionalInfo?.email || '';
        return `${name}${phone !== '--' ? ` (${phone})` : ''}${email ? ` [${email}]` : ''}`;
      },
    },
    {
      id: 'orderInfo',
      name: 'Order Info',
      selector: row => row?.orderUniqueId,
      width: 180,
      cell: row => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <span style={{ fontWeight: 700, color: '#34495e', fontSize: '13px' }}>
            {row?.orderUniqueId || '--'}
          </span>
          <span style={{ fontSize: '11px', color: '#7f8c8d' }}>
            {row?.createdAt ? moment(row.createdAt).format('DD MMM, hh:mm A') : '--'}
          </span>
          {row?.orderType && (
            <Tag style={{ width: 'fit-content', fontSize: '10px', marginTop: '2px', padding: '0 4px' }}>
              {row.orderType.toUpperCase()}
            </Tag>
          )}
        </div>
      ),
    },

    {
      id: 'bookingBooking',
      name: 'Booking Details',
      selector: row => row?.bookingFor,
      minWidth: 250,
      cell: row => {
        const info = row?.additionalInfo || {};
        const startDate = info?.startDate;
        const endDate = info?.endDate;
        const bookingDate = info?.bookingDate;
        const startTime = info?.startTime;

        // Determine specific items (Room, Service, Treatment)
        let specifics = info?.serviceName;
        if (!specifics && info?.roomDetails?.length > 0) {
          specifics = info.roomDetails.map(r => r.roomTypeName).join(', ');
        }
        if (!specifics && info?.treatmentData?.length > 0) {
          specifics = info.treatmentData.map(t => t.treatmentName).join(', ');
        }
        if (!specifics && info?.serviceData?.length > 0) {
          specifics = info.serviceData.map(s => s.name).join(', ');
        }

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontWeight: 600, color: '#E67E22', fontSize: '13px' }}>
                {row?.bookingFor || '--'}
              </span>
              {row?.isSimulation && (
                <Tooltip title={`Created By: ${info?.createdByName || row?.createdByName || 'System'}`}>
                  <Avatar src={PrakurtiCirculerLogo} size={16} />
                </Tooltip>
              )}
            </div>

            {/* Date / Time Display */}
            <div style={{ fontSize: '12px', color: '#555', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <CalendarOutlined style={{ fontSize: '11px' }} />
              {startDate && endDate ? (
                <span>{startDate} <span style={{ color: '#aaa' }}>to</span> {endDate}</span>
              ) : (
                <span>{bookingDate || '--'} {startTime && `at ${startTime}`}</span>
              )}
            </div>

            {/* Specifics (Room/Service Name) */}
            {specifics && (
              <div style={{ fontSize: '11px', color: '#7f8c8d', fontStyle: 'italic', lineHeight: '1.2' }}>
                {specifics}
              </div>
            )}

            {/* Simulation Creator Footer */}
            {(info?.createdByName || row?.createdByName) && (
              <div style={{ fontSize: '10px', color: '#aaa', marginTop: '2px' }}>
                By: {info?.createdByName || row?.createdByName}
              </div>
            )}
          </div>
        )
      },
      format: (value, row) => {
        const info = row?.additionalInfo || {};
        const bookingFor = row?.bookingFor || '--';
        const startDate = info?.startDate;
        const endDate = info?.endDate;
        const bookingDate = info?.bookingDate;
        const startTime = info?.startTime;

        let specifics = info?.serviceName;
        if (!specifics && info?.roomDetails?.length > 0) {
          specifics = info.roomDetails.map(r => r.roomTypeName).join(', ');
        }
        if (!specifics && info?.treatmentData?.length > 0) {
          specifics = info.treatmentData.map(t => t.treatmentName).join(', ');
        }
        if (!specifics && info?.serviceData?.length > 0) {
          specifics = info.serviceData.map(s => s.name).join(', ');
        }

        const datePart = startDate && endDate ? `${startDate} to ${endDate}` : `${bookingDate || '--'} ${startTime || ''}`;
        return `${bookingFor} | ${datePart}${specifics ? ` | ${specifics}` : ''}`;
      },
    },
    {
      id: 'financials',
      name: 'Billing',
      selector: row => row?.amount,
      width: 160,
      cell: row => {
        const billing = calculateBilling(row);

        if (!billing) return '--';

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', fontWeight: 600 }}>
              <Tooltip title="Total Gross Amount">
                <span style={{ color: '#7f8c8d', textDecoration: billing.discountValue > 0 ? 'line-through' : 'none', fontSize: '11px' }}>
                  {formatCurrency(billing.total)}
                </span>
              </Tooltip>
              {billing.discountRate > 0 && (
                <span style={{ color: '#27ae60', fontSize: '10px' }}>
                  -{billing.discountType === 'percentage' ? `${billing.discountRate}%` : formatCurrency(billing.discountRate)}
                </span>
              )}
            </div>

            <div style={{ fontSize: '14px', fontWeight: 700, color: '#2c3e50' }}>
              {formatCurrency(billing.netAmount)}
            </div>

            {!billing.isFullyPaid ? (
              <div style={{ color: '#e67e22', fontSize: '11px', fontWeight: 600, background: '#fff7ed', padding: '2px 6px', borderRadius: '4px', width: 'fit-content' }}>
                Pending: {formatCurrency(billing.pending)}
              </div>
            ) : (
              <div style={{ color: '#27ae60', fontSize: '11px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '3px' }}>
                <span style={{ background: '#f0fdf4', padding: '2px 6px', borderRadius: '4px' }}>Paid Full</span>
              </div>
            )}
          </div>
        );
      },
      format: (value, row) => {
        const billing = calculateBilling(row);
        if (!billing) return '--';
        return `Total: ₹${billing.total}, Discount: ₹${billing.discountValue}, Paid: ₹${billing.paid}, Pending: ₹${billing.pending}`;
      },
    },
    {
      id: 'status',
      name: 'Status',
      width: 140,
      cell: row => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {row?.paymentStatus && (
            <Tag color={getStatus(row.paymentStatus)} style={{ margin: 0, textAlign: 'center', fontSize: '11px', width: '100%' }}>
              Pay: {row.paymentStatus}
            </Tag>
          )}
          {row?.orderStatus && (
            <Tag color={getStatus(row.orderStatus)} style={{ margin: 0, textAlign: 'center', fontSize: '11px', width: '100%' }}>
              Ord: {row.orderStatus}
            </Tag>
          )}
        </div>
      )
    },
    {
      id: 'actions',
      name: 'Actions',
      alignment: 'center',
      width: 80,
      cell: row => (
        <Button
          type="primary"
          size="small"
          icon={<WalletOutlined />}
          onClick={() => {
            formik.resetForm();
            setPaymentLinkType('Cash');
            setPaymentLinkAmount('');
            const updatedPaid = row?.updatedPaidAmount;

            const CollectCashPayload = {
              ...row,
              orderId: row?.id || row?._id,
              updatedBy: userDetails?.[0]?._id,
              updateByName: userDetails?.[0]?.name,
              updatedPaymentMode: '',
              updatedPaidAmount: updatedPaid,
              completedPayment: row?.additionalInfo?.completedPayment,
              paymentStatus: '',
              discount: row?.discount || row?.additionalInfo?.discount || 0
            };
            setCollectModal(CollectCashPayload);
          }}
          style={{ fontSize: '12px' }}
        >
          Collect
        </Button>
      ),
    },
  ], [userDetails]);

  // 2. Filter Configuration
  const filterConfig = useMemo(() => [
    {
      id: 'parentName',
      label: 'Customer Name',
      type: 'text',
      placeholder: 'Search name...',
    },
    {
      id: 'phoneNo',
      label: 'Phone Number',
      type: 'text',
      placeholder: 'Search phone...',
    },
    {
      id: 'bookingFor',
      label: 'Booking Type',
      type: 'select',
      options: [
        { label: 'Treatment', value: 'Treatment' },
        { label: 'Consultation', value: 'Consultation' },
        { label: 'Detox', value: 'Detox' },
        { label: 'Service', value: 'Service' },
      ],
    },
    {
      id: 'amount',
      label: 'Amount',
      type: 'text',
      placeholder: 'Amount',
    },
    // Using single date filter as AdvancedTable standard for now, but API expects range probably
    // We will handle filtering in fetchData
    {
      id: 'orderDateRange',
      label: 'Order Date Range',
      type: 'dateRange',
      placeholder: 'Select Date Range',
      quickFilters: ['today', 'yesterday', 'last7days', 'last30days'],
    },
  ], []);

  // 3. Data Fetching
  const fetchData = useCallback(async ({ page, perPage, sortField, sortOrder, search, ...filters }) => {
    // Construct Query String for API
    const queryParams = new URLSearchParams();
    queryParams.append('page', page);
    queryParams.append('limit', perPage);

    // Filters
    if (filters.parentName) queryParams.append('parentName', filters.parentName);
    if (filters.phoneNo) queryParams.append('phoneNo', filters.phoneNo);
    if (filters.bookingFor) queryParams.append('bookingFor', filters.bookingFor);
    if (filters.amount) queryParams.append('amount', filters.amount);
    if (filters.paymentStatus) queryParams.append('paymentStatus', filters.paymentStatus);

    // Date Range Filter Logic
    // Date Range Filter Logic
    let startStr, endStr;

    // Check for flattened keys from AdvancedTable (e.g. orderDateRange_start, orderDateRange_end)
    if (filters.orderDateRange_start || filters.orderDateRange_end) {
      startStr = filters.orderDateRange_start ? moment(filters.orderDateRange_start).format('YYYY-MM-DD') : '';
      endStr = filters.orderDateRange_end ? moment(filters.orderDateRange_end).format('YYYY-MM-DD') : '';
    } else if (filters.orderDateRange) {
      // Handle legacy formats or direct object passing
      if (typeof filters.orderDateRange === 'string') {
        const parts = filters.orderDateRange.split(',');
        startStr = parts[0];
        endStr = parts[1] || parts[0];
      } else if (typeof filters.orderDateRange === 'object') {
        startStr = filters.orderDateRange.start ? moment(filters.orderDateRange.start).format('YYYY-MM-DD') : '';
        endStr = filters.orderDateRange.end ? moment(filters.orderDateRange.end).format('YYYY-MM-DD') : '';
      }
    }

    if (startStr && endStr && startStr === endStr) {
      // If both dates are same, send only start date
      queryParams.append('todayDate', startStr);
    } else {
      // Otherwise behave as usual
      if (startStr) queryParams.append('orderStartDate', startStr);
      if (endStr) queryParams.append('orderEndDate', endStr);
    }

    try {
      // Using /orders_pending endpoint as per original file logic
      const queryString = `/orders_pending?${queryParams.toString()}`;
      const response = await GetAllOrderApi({ Query: queryString });

      if (response?.status === 200) {
        // API structure check from original code: pendingOrder?.docs
        // GetAllOrderApi usually returns { data: { data: [{ docs: [], totalDocs: ... }] } }
        // We need to carefully extract it.
        // Based on OrderHistorySaga line 37: response?.data?.data?.[0] is the payload
        const payload = response?.data?.data?.[0];

        return {
          data: payload?.docs || [],
          total: payload?.totalDocs || 0,
        };
      }
      return { data: [], total: 0 };
    } catch (error) {
      console.error('Fetch Error:', error);
      return { data: [], total: 0 };
    }
  }, [refreshTrigger]);

  return (
    <>
      <Header />
      <Sidebar />
      <div className="page-wrapper">
        <div className="content">
          <div style={{ marginBottom: '16px' }}>
            <h3 style={{ fontWeight: 700, color: '#2c3e50' }}>Pending & History</h3>
            <small style={{ color: '#7f8c8d' }}>View pending orders and collect payments</small>
          </div>

          <AdvancedTable
            resource="PendingHistory"
            columns={columns}
            fetchData={fetchData}
            filterConfig={filterConfig}
            enableSearch={false}
            enableFilters={true}
            enableExport={true}
            enableRefresh={true}
            refreshTrigger={refreshTrigger}
            persistState={true}
          />

          {/* Collect Modal */}
          <Modal
            open={!!IsCollectModal?.orderId}
            closable={true}
            confirmLoading={Isloading}
            zIndex={1050}
            title={IsCollectModal?.additionalInfo?.customerName || "Collect Payment"}
            style={{ top: 20 }}
            width={500}
            onOk={() => handlePaymentLinkSubmit()}
            okText={'Submit'}
            centered
            onCancel={() => {
              setCollectModal(null);
              setPaymentLinkType('Cash');
              setPaymentLinkAmount('');
              setRemark('');
            }}
            styles={{
              content: { borderRadius: '12px', overflow: 'hidden' }
            }}
          >
            <div>
              <div style={{ marginBottom: 20 }}>
                <label style={{ fontWeight: 500, display: 'block', marginBottom: 8 }}>Payment Collection Method</label>
                <Radio.Group
                  value={paymentLinkType}
                  onChange={e => setPaymentLinkType(e.target.value)}
                  style={{ width: '100%' }}
                >
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Radio value="Cash">Collect Cash</Radio>
                    <Radio value="Email Link">Send Payment Link via Email</Radio>
                    <Radio value="WhatsApp Link">Send Payment Link via WhatsApp</Radio>
                  </Space>
                </Radio.Group>
              </div>

              {paymentLinkType === 'Cash' ? (
                <>
                  <div
                    style={{
                      padding: '16px',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '12px',
                      marginBottom: '20px',
                      border: '1px solid #e9ecef',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span style={{ color: '#6c757d' }}>Subtotal:</span>
                      <span style={{ fontWeight: '500' }}>₹{IsCollectModal?.amount}</span>
                    </div>
                    {discount > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', color: '#198754' }}>
                        <span>Discount:</span>
                        <span>- ₹{discount}</span>
                      </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', paddingTop: '8px', borderTop: '1px dashed #dee2e6', fontWeight: '700' }}>
                      <span>Payable Amount:</span>
                      <span>₹{payableAmount}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', color: '#0d6efd', fontSize: '13px' }}>
                      <span>Already Paid:</span>
                      <span>₹{IsCollectModal?.updatedPaidAmount || 0}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', color: '#d35400', fontSize: '13px', fontWeight: 600, marginTop: 4 }}>
                      <span>Pending:</span>
                      <span>₹{PendingAmt}</span>
                    </div>
                  </div>

                  <InputFromUser
                    FormGroupClassName={'mb-0'}
                    LabelClassName={'form-label'}
                    placeholder={'Enter Amount'}
                    FormControlClassName={'form-control'}
                    label={`Amount to Collect (Max: ₹${PendingAmt})`}
                    max={PendingAmt}
                    type={'number'}
                    name="amount"
                    error={formik?.errors?.updatedPaidAmount}
                    value={formik.values?.updatedPaidAmount}
                    touched={formik.touched.updatedPaidAmount}
                    onBlur={() => formik.setFieldTouched('updatedPaidAmount', true)}
                    onChange={e => {
                      const value = e?.target?.value;
                      if (value === '' || (Number(value) >= 0 && Number(value) <= PendingAmt)) {
                        formik.setFieldValue('updatedPaidAmount', value);
                      }
                    }}
                  />
                </>
              ) : (
                <div style={{ padding: '16px', backgroundColor: '#f0f7ff', borderRadius: '8px', border: '1px solid #d6e4ff' }}>
                  <InputFromUser
                    FormGroupClassName={'mb-0'}
                    LabelClassName={'form-label'}
                    placeholder={'Enter Amount'}
                    FormControlClassName={'form-control'}
                    label={`Link Amount (Max: ₹${PendingAmt})`}
                    max={PendingAmt}
                    type={'number'}
                    name="paymentLinkAmount"
                    value={paymentLinkAmount}
                    onChange={e => {
                      const value = e?.target?.value;
                      if (value === '' || (Number(value) >= 0 && Number(value) <= PendingAmt)) {
                        setPaymentLinkAmount(value);
                      }
                    }}
                  />

                  <div style={{ marginTop: 16 }}>
                    <p style={{ margin: '4px 0', fontSize: '13px' }}>
                      <strong>Customer:</strong> {IsCollectModal?.additionalInfo?.customerName || IsCollectModal?.additionalInfo?.name}
                    </p>
                    {paymentLinkType === 'Email Link' && (
                      <p style={{ margin: '4px 0', fontSize: '13px' }}>
                        <strong>Email:</strong> {IsCollectModal?.additionalInfo?.email || 'Not available'}
                      </p>
                    )}
                    {paymentLinkType === 'WhatsApp Link' && (
                      <p style={{ margin: '4px 0', fontSize: '13px' }}>
                        <strong>Phone:</strong> {IsCollectModal?.additionalInfo?.phoneNo || 'Not available'}
                      </p>
                    )}
                  </div>

                  <div style={{ marginTop: 12, fontSize: '12px', color: '#666', display: 'flex', gap: '8px' }}>
                    <InfoCircleOutlined />
                    <span>
                      {paymentLinkType === 'Email Link'
                        ? 'A secure payment link will be sent to the customer\'s email.'
                        : 'A secure payment link will be sent to the customer\'s WhatsApp.'}
                    </span>
                  </div>
                </div>
              )}

              <div style={{ marginTop: 20 }}>
                <InputFromUser
                  type="textarea"
                  label="Remark / Note"
                  placeholder="Enter any internal notes or remarks..."
                  name="remark"
                  value={remark}
                  onChange={e => setRemark(e.target.value)}
                  FormGroupClassName="mb-0"
                  LabelClassName="form-label"
                  FormControlClassName="form-control"
                />
              </div>
            </div>
          </Modal>
        </div>
      </div>
    </>
  );
};

export default PendingHistory;
